/**
 * Trustico® Hosted SSL Certificate Issuance Tool - localStorage state
 * persistence layer.
 *
 * Post-pivot (2026-05-21) the browser holds no cryptographic state
 * the customer did not enter on the page ; every signed Automatic
 * Certificate Management Environment (ACME) call is made by the
 * Trustico® server. This module persists only what the page needs
 * to resume after a reload :
 *
 *   - serverId         - selected ACME service id (non-secret)
 *   - sessionId        - server session identifier (non-secret ; the
 *                        server-side session holds the keys + EAB
 *                        + order URL)
 *   - currentStep      - last visible step index
 *   - completedSteps   - array of completed step indices
 *
 * IMPORTANT - what we deliberately do NOT persist (2026-05-31 code
 * review §1 fix) :
 *
 *   - loadedConfig - the parsed Trustico® Configuration File body.
 *                    It contains the ACME account private key, the
 *                    SSL Certificate private key, and the EAB
 *                    credentials. Persisting it would let any
 *                    Cross-Site Scripting (XSS) flaw in the page
 *                    exfiltrate enough material to issue further
 *                    SSL Certificates against the customer's
 *                    Trustico® subscription. If the customer
 *                    reloads mid-flow they re-drop the file ; the
 *                    server-held session keyed by sessionId still
 *                    has every secret it needs to continue.
 *
 * The page restore path calls /api/issuance/sessions/:id to verify
 * the session is still live. Sessions survive server restarts since
 * the 2026-07-03 Valkey migration and live 48 hours from CREATION -
 * an absolute cap (cappedSessionTtlMs in the server's
 * sessionStore.js) : activity refreshes the store expiry only within
 * that cap and never extends a session's life (2026-08-14
 * comment-accuracy sweep : this header previously said "48 hours
 * after their last write", the same overstatement the store's own
 * header corrected - a support answer built on it would tell a
 * customer active use keeps the order alive, and day three would
 * prove it wrong). When the lookup DEFINITIVELY 404s (expired or
 * discarded) the page wipes localStorage and starts fresh ;
 * transient lookup failures keep the pointer and offer Retry.
 *
 * Namespaced under STATE_STORAGE_KEY so future schema changes ignore
 * older payloads instead of crashing on them.
 */

import { STATE_STORAGE_KEY } from "../../shared/issuance/config.js";

const SCHEMA_VERSION = 2;

/**
 * Persist the page state to localStorage. Silent no-op when
 * localStorage is unavailable (private mode, quota exceeded) -
 * saving is a convenience, not a correctness requirement.
 *
 * Shape of the input :
 *   { currentStep, completedSteps,
 *     step1: { serverId } | null,
 *     step2: { sessionId } | null }
 *
 * NOTE : step1 is intentionally serverId-only. The caller may pass a
 * loadedConfig field for in-memory use during the same page lifetime,
 * but this function strips it before persisting - secret material
 * (ACME account private key, SSL Certificate private key, EAB
 * credentials) must never reach localStorage. See the file-header
 * note in this module.
 */
export function saveState({ currentStep, completedSteps, step1, step2 }) {
  if (typeof localStorage === "undefined") return;
  const payload = {
    schemaVersion: SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    currentStep,
    completedSteps,
    // Persist serverId only. Any loadedConfig on `step1` is dropped
    // here - see the file-header note for the rationale (XSS
    // exfiltration of the customer's ACME account key + EAB HMAC).
    step1: step1 ? { serverId: step1.serverId } : null,
    step2: step2 ? { sessionId: step2.sessionId } : null,
  };
  try {
    localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore - quota exceeded, private mode, etc.
  }
}

/**
 * Read the saved state from localStorage, or return null when
 * nothing is stored or the schema version doesn't match. The page
 * caller still has to call /api/issuance/sessions/:id to verify the
 * sessionId is alive on the server.
 */
export function loadState() {
  if (typeof localStorage === "undefined") return null;
  let raw;
  try {
    raw = localStorage.getItem(STATE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.schemaVersion === SCHEMA_VERSION) return parsed;
    return null;
  } catch {
    return null;
  }
}

/**
 * Remove the saved state. Called on Reset and on a failed restore.
 */
export function clearState() {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(STATE_STORAGE_KEY);
  } catch {
    // ignore
  }
}
